//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { Search, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent } from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import UserProfileList, { User, UserProfileListRef } from './UserProfileList';
import UserProfileDetails from './UserProfileDetails';
import FilterBar, { FilterState } from './FilterDropdown';
import TenantDataService from '../../utils/TenantDataService';
import LicenseDataService from '../../utils/LicenseDataService';
import { useTranslation } from 'react-i18next';
import LicenseDisplay from './LicenseDisplay';
import { useUserAuth } from '../../UserAuth';
import CustomSnackbar, { useSnackbar } from '../common/CustomSnackbar';
import { filterValidTeamspaceNames } from '../../utils/teamspaceUtils';
import { FetchKeycloakUserResponse } from '../Sidebar/SidebarTypes';

interface ApiUser {
  id: string;
  username: string;
  email: string;
  userid: string;
  firstname: string | null;
  lastname: string | null;
  inDefaultTeamSpace: string | null;
  roles: string[];
  password: null;
  authorities: { authority: string }[];
  enabled: boolean;
  credentialsNonExpired: boolean;
  accountNonExpired: boolean;
  accountNonLocked: boolean;
  teamspaces?: string[];
  loops?: string[];
  tier?: string;
  _links: { [key: string]: { href: string } };
}

interface ExtendedUser extends User {
  tier?: string;
}
interface LicenseInfo {
  tier: string;
  used: number;
  total: number;
  remaining: number;
  color: string;
}
interface LicenseTierData {
  selectedTier: string;
  tiers: LicenseInfo[];
  mode?: string;
}
/**
 * Loop memberships for the tenant, fetched ONCE by UserAdminLayout and streamed
 * to this panel as the background fan-out progresses.
 */
export interface UserLoopsData {
  /** Lower-cased username -> loop display names the user belongs to. */
  byUser: Record<string, string[]>;
  /** Distinct loop names seen so far, for the Loops filter dropdown. */
  loopNames: string[];
  /** True while the fan-out is still running. */
  loading: boolean;
}

export interface UsersPanelProps {
  licenseData: LicenseTierData;
  onLicenseChange?: () => void | Promise<void>; // callback to refresh license counts
  /** Loop memberships from the parent; undefined means "not available". */
  loopData?: UserLoopsData;
  /**
   * Asks the parent to re-run the loop fan-out after a manual refresh. Receives
   * the freshly fetched usernames so the parent covers users added or removed
   * since the page loaded.
   */
  onRefreshLoops?: (usernames: string[]) => void;
}
interface LicenseResponse {
  selectedTier: string;
  assigned: { [userId: string]: string };
  features: {
    [tier: string]: {
      total: number;
      leased: number;
      userIds: string[];
    };
  };
  mode: string;
}

// Map backend tier codes to human-friendly names for UI messages
const TIER_DISPLAY_NAMES: Record<string, string> = {
  'nexus-t1': 'Essentials',
  'nexus-t2': 'Standard',
  'nexus-t3': 'Premium',
};

const getTierDisplayName = (code: string, noneLabel?: string): string =>
  TIER_DISPLAY_NAMES[code] || noneLabel || code;

type AlertDialogState = {
  open: boolean;
  title: string;
  message: string;
  actionType: 'revoke';
  userToProcess: ExtendedUser | null;
  onConfirm: () => void;
};

const UsersPanel: React.FC<UsersPanelProps> = ({
  licenseData,
  onLicenseChange,
  loopData,
  onRefreshLoops,
}) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useUserAuth();
  const {
    snackbarState,
    showSnackbar,
    closeSnackbar,
  }: {
    snackbarState: {
      open: boolean;
      message: string;
      severity: 'success' | 'error' | 'warning' | 'info';
    };
    showSnackbar: (
      message: string,
      severity?: 'success' | 'error' | 'warning' | 'info',
    ) => void;
    closeSnackbar: () => void;
  } = useSnackbar();
  const tenantDataService = useMemo(
    () =>
      new TenantDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );
  const licenseService = useMemo(
    () =>
      new LicenseDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );
  const currentUserId = user?.profile?.sub || '';
  const currentUserEmail = user?.profile?.email || '';
  const userProfileListRef = useRef<UserProfileListRef>(null);

  const [selectedUser, setSelectedUser] = useState<ExtendedUser | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<ExtendedUser[]>([]);
  const [apiUsers, setApiUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localLicenseInfo, setLocalLicenseInfo] = useState<{
    remaining: number;
    total: number;
  } | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    teamspace: [],
    loops: [],
    licenses: [],
    role: [],
  });
  const [prefetchedTeamspaceNames, setPrefetchedTeamspaceNames] = useState<
    string[]
  >([]);

  // Loop names for a user from the parent's (possibly still loading) loop data.
  // Re-created whenever loopData changes; fetchUsers always sees the latest
  // version through fetchUsersRef, which is refreshed on every render.
  const loopNamesFor = useCallback(
    (username: string | undefined): string[] =>
      (username && loopData?.byUser[username.toLowerCase()]) || [],
    [loopData],
  );

  const [alertDialog, setAlertDialog] = useState<AlertDialogState>({
    open: false,
    title: t('alert'),
    message: '',
    actionType: 'revoke',
    userToProcess: null,
    onConfirm: () => {},
  });

  const isCurrentUser = useCallback(
    (u: User) => u.id === currentUserId || u.email === currentUserEmail,
    [currentUserId, currentUserEmail],
  );
  const getInitials = (
    f?: string | null,
    l?: string | null,
    un: string = '',
  ) => (f && l ? `${f[0]}${l[0]}` : un.substring(0, 2).toUpperCase());

  const convertApiUserToUser = (
    api: ApiUser,
    assignedTier?: string,
  ): ExtendedUser => {
    const tier = assignedTier || api.tier || 'none';
    const enabled = tier !== 'none';

    return {
      id: api.id,
      name:
        api.firstname && api.lastname
          ? `${api.firstname} ${api.lastname}`
          : api.username,
      email: api.email,
      initials: getInitials(api.firstname, api.lastname, api.username),
      action: enabled ? 'Revoke' : 'Assign',
      username: api.username,
      userid: api.userid,
      firstname: api.firstname,
      lastname: api.lastname,
      enabled: enabled,
      roles: api.roles || [],
      teamspaces: api.teamspaces || [],
      loops: api.loops || [],
      tier: tier,
    };
  };

  /** Loads the user list. Resolves to the fetched usernames, or null on failure. */
  const fetchUsers = async (): Promise<string[] | null> => {
    try {
      setLoading(true);

      const [
        userResponse,
        licenseResponse,
        teamspaceMappingsResponse,
        keycloakUsersResponse,
      ] = await Promise.all([
        tenantDataService.getAllUsers(),
        licenseService.getLicenses(),
        tenantDataService.getUserTeamspaceMappings().catch(() => ({})),
        tenantDataService.fetchKeycloakUsers().catch(() => []),
      ]);

      const licenses = licenseResponse as LicenseResponse;
      // Compute local license totals from the response
      if (licenses.features) {
        let totalLicenses = 0;
        let leasedLicenses = 0;
        Object.entries(licenses.features).forEach(([key, val]) => {
          if (key.startsWith('nexus-t')) {
            totalLicenses += val.total || 0;
            leasedLicenses += val.leased || 0;
          }
        });
        setLocalLicenseInfo({
          remaining: totalLicenses - leasedLicenses,
          total: totalLicenses,
        });
      }
      const apiUserList: ApiUser[] =
        (userResponse as any)?._embedded?.users || [];
      const userTeamspaceMappings = (teamspaceMappingsResponse || {}) as Record<
        string,
        string[]
      >;
      const keycloakNameByUsername = new Map<
        string,
        { firstname: string; lastname: string }
      >(
        ((keycloakUsersResponse || []) as FetchKeycloakUserResponse).map(
          (ku) => [
            ku.username.toLowerCase(),
            { firstname: ku.firstname, lastname: ku.lastname },
          ],
        ),
      );

      // Loops are NOT fetched here. GET /loops without userId is restricted
      // server-side to the admin's own memberships, so loop data has to be
      // gathered per user; UserAdminLayout does that once, in the background,
      // and streams it in via the `loopData` prop (see the effect below). This
      // panel therefore renders as soon as the four calls above return.

      // Enrich each user with teamspaces from batch mapping and whatever loop
      // data the parent has so far.
      const enriched: ApiUser[] = apiUserList.map((apiUser: ApiUser) => {
        const assignedTier = licenses.assigned?.[apiUser.id] || 'none';
        // userTeamspaceMappings is the authoritative source for user-teamspace
        // associations. inDefaultTeamSpace is a boolean flag ("true"/"false"),
        // not a teamspace name, so it must not be used as a fallback here.
        const rawTeamspaces =
          userTeamspaceMappings[apiUser.id] &&
          userTeamspaceMappings[apiUser.id].length > 0
            ? userTeamspaceMappings[apiUser.id]
            : [];
        const teamspaces = filterValidTeamspaceNames(rawTeamspaces);
        const loops = loopNamesFor(apiUser.username);
        const liveName = keycloakNameByUsername.get(
          apiUser.username.toLowerCase(),
        );
        return {
          ...apiUser,
          firstname: liveName?.firstname || apiUser.firstname,
          lastname: liveName?.lastname || apiUser.lastname,
          teamspaces,
          loops,
          tier: assignedTier,
          enabled: assignedTier !== 'none',
        };
      });

      // Pre-fetch filter names from batch mapping + any inDefaultTeamSpace values.
      // Filter out invalid teamspace names (like "true", "false", empty strings).
      const tsNamesFromUsers = new Set<string>();
      for (const names of Object.values(userTeamspaceMappings)) {
        if (Array.isArray(names)) {
          const validNames = filterValidTeamspaceNames(names);
          validNames.forEach((t) => tsNamesFromUsers.add(t));
        }
      }
      setPrefetchedTeamspaceNames(Array.from(tsNamesFromUsers));

      // Render user list NOW – no waiting for loop resolution.
      setApiUsers(enriched);
      const transformed = enriched.map((u: ApiUser) =>
        convertApiUserToUser(u, u.tier),
      );
      setUsers(transformed);
      if (transformed.length) setSelectedUser(transformed[0]);
      setLoading(false);
      return apiUserList.map((u) => u.username).filter((n) => !!n);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(t('failed-fetch-users'));
      setApiUsers([]);
      setUsers([]);
      setSelectedUser(null);
      setLoading(false);
      return null;
    }
  };

  const fetchUsersRef = useRef(fetchUsers);
  useEffect(() => {
    fetchUsersRef.current = fetchUsers;
  });
  useEffect(() => {
    fetchUsersRef.current();
  }, []);

  // Loop memberships are DERIVED at render time from the parent's loopData
  // rather than copied into state, so each background publish from the
  // fan-out re-maps the lists without an extra state round-trip. Only the
  // views that display or filter on loops use these derived versions; the
  // license-management handlers keep working on the underlying state.
  const withLoops = useCallback(
    <T extends { username?: string; loops?: string[] }>(u: T): T =>
      loopData ? { ...u, loops: loopNamesFor(u.username) } : u,
    [loopData, loopNamesFor],
  );
  const apiUsersWithLoops = useMemo(
    () => apiUsers.map(withLoops),
    [apiUsers, withLoops],
  );
  const selectedUserWithLoops = useMemo(
    () => (selectedUser ? withLoops(selectedUser) : null),
    [selectedUser, withLoops],
  );

  // Memoize filtered users to avoid recalculations on every render
  const filteredApiUsers = useMemo(() => {
    let filtered = [...apiUsersWithLoops];
    if (filters.teamspace.length) {
      if (filters.teamspace.includes('__NONE_SELECTED__')) return [];
      filtered = filtered.filter((u) =>
        u.teamspaces?.some((ts) => filters.teamspace.includes(ts)),
      );
    }
    if (filters.loops.length) {
      if (filters.loops.includes('__NONE_SELECTED__')) return [];
      filtered = filtered.filter((u) =>
        u.loops?.some((lp) =>
          filters.loops
            .map((l) => l.trim().toLowerCase())
            .includes((lp || '').trim().toLowerCase()),
        ),
      );
    }
    if (filters.licenses.length)
      filtered = filtered.filter((u) =>
        filters.licenses.includes(u.enabled ? 'Yes' : 'No'),
      );
    if (filters.role.length)
      filtered = filtered.filter((u) => {
        const cu = convertApiUserToUser(u, u.tier);
        if (filters.role.includes('Admin') && isCurrentUser(cu)) return true;
        if (filters.role.includes('User') && !isCurrentUser(cu)) return true;
        return false;
      });
    return filtered;
  }, [apiUsersWithLoops, filters, isCurrentUser]);

  const getFilteredUsers = (): ApiUser[] => filteredApiUsers;

  const handleAssignLicenseFromDropdown = async (
    userId: string,
    tier: string,
  ) => {
    const u = apiUsers.find((x) => x.id === userId);
    if (!u) throw new Error('User not found');

    // Prevent current user from revoking their own license
    if (isCurrentUser(convertApiUserToUser(u, u.tier)) && tier === 'none') {
      showSnackbar(t('cannot-revoke-own-license'), 'warning');
      throw new Error('Cannot revoke own license');
    }

    try {
      await licenseService.manageLicense(userId, tier);

      const shouldEnable = tier !== 'none';
      if (shouldEnable) {
        await tenantDataService.enableUserInKeycloak(u.username);
      } else {
        await tenantDataService.disableUserInKeycloak(u.username);
      }

      setApiUsers((prev) =>
        prev.map((x) =>
          x.id === userId ? { ...x, enabled: shouldEnable, tier } : x,
        ),
      );
      setUsers((prev) =>
        prev.map((x) =>
          x.id === userId
            ? {
                ...x,
                enabled: shouldEnable,
                tier,
                action: shouldEnable ? 'Revoke' : 'Assign',
              }
            : x,
        ),
      );
      if (selectedUser?.id === userId)
        setSelectedUser((prev) =>
          prev
            ? {
                ...prev,
                enabled: shouldEnable,
                tier,
                action: shouldEnable ? 'Revoke' : 'Assign',
              }
            : null,
        );

      // After successful local state update, trigger parent license count refresh
      if (onLicenseChange) {
        try {
          await onLicenseChange();
        } catch (refreshErr) {}
      }

      return { success: true };
    } catch (e: any) {
      const status = e?.response?.status ?? e?.status;
      if (status === 402) {
        // No licenses of requested tier available
        showSnackbar(
          t('no-licenses-available', { tier: getTierDisplayName(tier) }),
          'error',
        );
        throw e;
      }
      if (status === 503) {
        // License service unavailable
        showSnackbar(t('license-server-connection-failed'), 'error');
        throw e;
      }

      throw e;
    }
  };

  // Keep a stable ref to handleAssignLicenseFromDropdown so callbacks never capture a stale version.
  const handleAssignLicenseRef = useRef<
    | ((
        userId: string,
        tier: string,
      ) => Promise<{ success: boolean } | undefined>)
    | null
  >(null);
  useEffect(() => {
    handleAssignLicenseRef.current = handleAssignLicenseFromDropdown;
  });

  const handleConfirmRevokeFromDropdown = useCallback(
    (user: User, newTier: string) => {
      const extendedUser = users.find((x) => x.id === user.id);
      if (!extendedUser) return;

      setAlertDialog({
        open: true,
        title: t('alert'),
        message: t('revoke-license-confirm', { name: user.name }),
        actionType: 'revoke',
        userToProcess: extendedUser,
        onConfirm: async () => {
          try {
            await handleAssignLicenseRef.current!(user.id, newTier);

            const updatedUser = {
              ...user,
              tier: newTier,
              enabled: false,
              action: 'Assign' as const,
            };

            if (userProfileListRef.current) {
              userProfileListRef.current.updateUserState(updatedUser);
            }

            showSnackbar(
              t('license-revoked-success', { name: user.name }),
              'success',
            );
          } catch (e) {
            showSnackbar(t('failed-to-update-license'), 'error');
          }
        },
      });
    },
    [users, t, showSnackbar],
  );
  const handleUserSelect = useCallback(
    (u: User) => {
      const ext = users.find((x) => x.id === u.id);
      if (ext) setSelectedUser(ext);
    },
    [users],
  );
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value),
    [],
  );
  const handleSearchReset = () => {
    setSearchQuery('');
    setFilters({ teamspace: [], loops: [], licenses: [], role: [] });
    // Re-fetch users, then hand the fresh username list to the parent so the
    // loop fan-out covers users added/removed since the page loaded.
    void fetchUsers().then((usernames) => {
      if (usernames) onRefreshLoops?.(usernames);
    });
    // Also refresh license counts in parent when user triggers a manual refresh
    if (onLicenseChange) {
      Promise.resolve(onLicenseChange()).catch((e) => {});
    }
  };
  const handleFiltersChange = (f: FilterState) => setFilters(f);
  const showSuccessAlert = (m: string) => showSnackbar(m, 'success');
  const showErrorAlert = (m: string) => showSnackbar(m, 'error');

  const handleConfirmDialog = () => {
    alertDialog.onConfirm();
    setAlertDialog({ ...alertDialog, open: false, userToProcess: null });
  };

  const handleCloseAlertDialog = () => {
    setAlertDialog({ ...alertDialog, open: false, userToProcess: null });
  };

  const handleRevokeLicense = () => {
    if (!selectedUser) return;

    // Check if trying to revoke own license
    if (isCurrentUser(selectedUser)) {
      showSnackbar(t('cannot-revoke-own-license'), 'warning');
      return;
    }

    setAlertDialog({
      open: true,
      title: t('alert'),
      message: t('revoke-license-confirm', { name: selectedUser.name }),
      actionType: 'revoke',
      userToProcess: selectedUser,
      onConfirm: () => {
        processRevokeLicense(selectedUser);
      },
    });
  };

  const processRevokeLicense = async (user: ExtendedUser) => {
    try {
      await handleAssignLicenseFromDropdown(user.id, 'none');

      const updatedUser = {
        ...user,
        enabled: false,
        action: 'Assign' as const,
        tier: 'none',
      };

      setUsers((prev) => prev.map((u) => (u.id === user.id ? updatedUser : u)));
      if (selectedUser?.id === user.id) {
        setSelectedUser(updatedUser);
      }
      setApiUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, enabled: false, tier: 'none' } : u,
        ),
      );

      if (userProfileListRef.current) {
        userProfileListRef.current.updateUserState(updatedUser);
      }

      showSuccessAlert(t('license-revoked-success', { name: user.name }));
    } catch (e) {
      showErrorAlert(t('failed-to-update-license'));
    }
  };

  const handleAssignLicense = async () => {
    if (!selectedUser) return;
    const nextTier =
      selectedUser.tier && selectedUser.tier !== 'none'
        ? selectedUser.tier
        : 'nexus-t1';
    try {
      await handleAssignLicenseFromDropdown(selectedUser.id, nextTier);

      const updatedUser = {
        ...selectedUser,
        enabled: true,
        action: 'Revoke' as const,
        tier: nextTier,
      };

      setUsers((prev) =>
        prev.map((u) => (u.id === selectedUser.id ? updatedUser : u)),
      );
      setSelectedUser(updatedUser);
      setApiUsers((prev) =>
        prev.map((u) =>
          u.id === selectedUser.id
            ? { ...u, enabled: true, tier: nextTier }
            : u,
        ),
      );

      if (userProfileListRef.current) {
        userProfileListRef.current.updateUserState(updatedUser);
      }

      showSuccessAlert(
        t('license-assigned-success', {
          name: selectedUser.name,
          tier: getTierDisplayName(nextTier),
        }),
      );
    } catch (e) {
      showErrorAlert(t('failed-to-update-license'));
    }
  };

  return (
    <TooltipProvider>
      <div id="id-users-panel-container" data-testid="users-panel-container">
        {licenseData.mode === 'named' && (
          <div
            id="id-users-panel-license-wrapper"
            data-testid="users-panel-license-wrapper"
            className="mb-4"
          >
            <LicenseDisplay licenseData={licenseData} />
          </div>
        )}
        <div
          id="id-users-panel-toolbar"
          data-testid="users-panel-toolbar"
          className="mb-6 flex flex-wrap items-center gap-4 border-b border-gray-200 pb-4 dark:border-gray-600"
        >
          <div
            id="id-users-panel-filter-section"
            data-testid="users-panel-filter-section"
            className="flex min-w-[160px] flex-[1_1_160px] items-center gap-6"
          >
            <FilterBar
              onFiltersChange={handleFiltersChange}
              filters={filters}
              prefetchedTeamspaceNames={prefetchedTeamspaceNames}
              prefetchedLoopNames={loopData?.loopNames ?? []}
              aria-label="Filter"
            />
            {(() => {
              const remaining =
                licenseData.tiers.length > 0
                  ? licenseData.tiers.reduce((sum, t) => sum + t.remaining, 0)
                  : localLicenseInfo?.remaining;
              const total =
                licenseData.tiers.length > 0
                  ? licenseData.tiers.reduce((sum, t) => sum + t.total, 0)
                  : localLicenseInfo?.total;
              return remaining != null && total != null ? (
                <p className="whitespace-nowrap text-sm text-carbon-textPrimary dark:text-gray-300">
                  <span
                    id="id-users-panel-remaining-count"
                    data-testid="users-panel-remaining-count"
                    className="font-medium text-orange-500"
                  >
                    {remaining}
                  </span>
                  {' of '}
                  <span
                    id="id-users-panel-total-count"
                    data-testid="users-panel-total-count"
                    className="font-medium text-orange-500"
                  >
                    {total}
                  </span>
                  {' licenses left'}
                </p>
              ) : null;
            })()}
          </div>
          <div
            id="id-users-panel-search-section"
            data-testid="users-panel-search-section"
            className="ml-auto flex items-center gap-2"
          >
            <div
              id="id-users-panel-search-wrapper"
              data-testid="users-panel-search-wrapper"
              className="relative"
            >
              <Search
                size={16}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500"
              />
              <Input
                type="text"
                placeholder={t('search-placeholder')}
                value={searchQuery}
                onChange={handleSearchChange}
                className="h-8 w-64 border-carbon-tableHeaderBg bg-transparent pl-8 text-sm"
              />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="rounded border border-carbon-tooltipText p-1.5 transition-colors hover:bg-gray-50 dark:border-gray-500 dark:hover:bg-gray-700"
                  onClick={handleSearchReset}
                  aria-label="Refresh"
                  id="id-refresh-icon"
                  data-testid="refresh-icon"
                >
                  <RefreshCw
                    size={16}
                    className="text-carbon-tooltipText dark:text-gray-300"
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('refresh')}</TooltipContent>
            </Tooltip>
          </div>
        </div>
        <div
          id="id-users-panel-content"
          data-testid="users-panel-content"
          className="flex h-[500px] gap-4"
        >
          <div
            id="id-users-panel-sidebar"
            data-testid="users-panel-sidebar"
            className="w-1/4 min-w-[200px] overflow-y-auto border-r border-gray-200 pr-4 dark:border-gray-600"
          >
            {loading ? (
              <div
                id="id-users-panel-loading"
                data-testid="users-panel-loading"
                className="flex items-center justify-center p-8"
              >
                <p className="text-sm text-gray-500">{t('loading-users')}</p>
              </div>
            ) : error ? (
              <div
                id="id-users-panel-error"
                data-testid="users-panel-error"
                className="flex items-center justify-center p-8"
              >
                <p className="text-sm text-red-500">{error}</p>
              </div>
            ) : (
              <UserProfileList
                ref={userProfileListRef}
                users={getFilteredUsers()}
                onUserSelect={handleUserSelect}
                searchQuery={searchQuery}
                assignLicense={handleAssignLicenseFromDropdown}
                defaultSelectedUserId={selectedUser?.id}
                isCurrentUserFn={isCurrentUser}
                onConfirmRevoke={handleConfirmRevokeFromDropdown}
              />
            )}
          </div>
          <div
            id="id-users-panel-detail-area"
            data-testid="users-panel-detail-area"
            className="flex-1 overflow-y-auto"
          >
            {selectedUser ? (
              <>
                <div
                  id="id-users-panel-user-header"
                  data-testid="users-panel-user-header"
                  className="mb-4 flex items-center gap-3"
                >
                  <div
                    id="id-users-panel-avatar"
                    data-testid="users-panel-avatar"
                    className="flex size-10 items-center justify-center rounded-full bg-carbon-surfaceSecondary text-sm font-medium text-carbon-textPrimary dark:bg-carbon-darkHeader dark:text-white"
                  >
                    {selectedUser.initials}
                  </div>
                  <h2 className="text-lg font-medium text-carbon-textPrimary dark:text-white">
                    {selectedUser.name}
                    {isCurrentUser(selectedUser) && (
                      <span
                        id="id-users-panel-you-badge"
                        data-testid="users-panel-you-badge"
                        className="ml-2 text-xs text-blue-600"
                      >
                        ({t('you')})
                      </span>
                    )}
                  </h2>
                </div>
                <div
                  id="id-users-panel-profile-wrapper"
                  data-testid="users-panel-profile-wrapper"
                >
                  <UserProfileDetails
                    userProp={selectedUserWithLoops ?? selectedUser}
                    onRevokeLicense={handleRevokeLicense}
                    onAssignLicense={handleAssignLicense}
                    isCurrentUser={isCurrentUser(selectedUser)}
                    loopsLoading={!!loopData?.loading}
                  />
                </div>
              </>
            ) : (
              <div
                id="id-users-panel-select-prompt"
                data-testid="users-panel-select-prompt"
                className="flex items-center justify-center p-8"
              >
                <p className="text-sm text-gray-500">{t('select-user')}</p>
              </div>
            )}
          </div>
        </div>

        <Dialog
          open={alertDialog.open}
          onOpenChange={(open) => {
            if (!open) handleCloseAlertDialog();
          }}
        >
          <DialogContent className="max-w-md">
            <div
              id="id-users-panel-dialog-accent"
              data-testid="users-panel-dialog-accent"
              className="-mx-6 -mt-6 mb-4 h-1 rounded-t-lg bg-[#FF8C00]"
            />
            <div
              id="id-users-panel-dialog-header"
              data-testid="users-panel-dialog-header"
              className="mb-4 flex items-center gap-2"
            >
              <AlertTriangle size={20} className="text-[#FF8C00]" />
              <h3 className="text-lg font-semibold">{alertDialog.title}</h3>
            </div>
            <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">
              {alertDialog.message}
            </p>
            <div
              id="id-users-panel-dialog-actions"
              data-testid="users-panel-dialog-actions"
              className="flex justify-end gap-3"
            >
              <Button variant="outline" onClick={handleCloseAlertDialog}>
                {t('cancel')}
              </Button>
              <Button onClick={handleConfirmDialog}>
                {alertDialog.actionType === 'revoke'
                  ? t('yes-revoke')
                  : t('yes-confirm')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <CustomSnackbar
          open={snackbarState.open}
          message={snackbarState.message}
          severity={snackbarState.severity}
          onClose={closeSnackbar}
        />
      </div>
    </TooltipProvider>
  );
};

export default UsersPanel;
